'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridDenseEdgesProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridDenseEdges - Small-cell grid pattern visible on edges
 *
 * Continuous 1px lines with 10px cells visible around the edges,
 * fading toward the center. Dense technical framing effect.
 *
 * Fixed fadeSize: 0.75
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridDenseEdges />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridDenseEdges({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridDenseEdgesProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.75
  const size = `${fadeSize * 100}%`
  const stop1 = `${fadeSize * 100 * 0.5}%`
  const stop2 = `${fadeSize * 100 * 0.9}%`
  const stop3 = `${fadeSize * 100 * 1.2}%`
  const maskGradient = `radial-gradient(ellipse ${size} ${size} at 50% 50%, transparent 0%, transparent ${stop1}, rgba(0,0,0,0.3) ${stop2}, rgba(0,0,0,0.6) ${stop3}, #000 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, color-mix(in srgb, var(--border), transparent 65%) 1px, transparent 1px),
          linear-gradient(to bottom, color-mix(in srgb, var(--border), transparent 65%) 1px, transparent 1px)
        `,
        backgroundSize: '10px 10px',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
